import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";
import {
  TherapistSlot,
  useTherapistSlots as useTherapistSlotsQuery,
} from "@/queries/group-visit/use-therapist-slots";
import { GroupVisitSessionModeEnum } from "@/services";

export type UseTherapistSlotsForDay = {
  slots: TherapistSlot[];
  isLoading: boolean;
};

export type TherapistSlotSearch = {
  groupVisitId?: string;
  providerIds: string[];
  availabilityMode: GroupVisitSessionModeEnum;
  locationUuid?: string;
  /** Slot length in minutes. */
  duration?: number;
};

type DayWindow = { startDate?: string; endDate?: string };

/** Bounds of the chosen day; `dayStart` is already midnight. */
const getDayWindow = (dayStart: Dayjs | null): DayWindow =>
  dayStart
    ? {
        startDate: dayStart.toISOString(),
        endDate: dayStart.add(1, "day").toISOString(),
      }
    : {};

/** Therapist slots for the day picked in the availability calendar. */
export const useTherapistSlotsForDay = (
  {
    groupVisitId,
    providerIds,
    availabilityMode,
    locationUuid,
    duration,
  }: TherapistSlotSearch,
  date: Dayjs | null
): UseTherapistSlotsForDay => {
  // Keyed on the day's timestamp so a re-created Dayjs for the same day doesn't refetch.
  const dayStart = date?.isValid() ? date.startOf("day").valueOf() : null;
  const { startDate, endDate } = useMemo(
    () => getDayWindow(dayStart === null ? null : dayjs(dayStart)),
    [dayStart]
  );

  const { data, isLoading, isFetching } = useTherapistSlotsQuery({
    groupVisitId,
    providerIds,
    startDate,
    endDate,
    availabilityMode,
    locationUuid,
    duration,
  });

  const slots = useMemo<TherapistSlot[]>(
    () =>
      dayStart === null
        ? []
        : (data ?? []).filter((slot) =>
            dayjs(slot.startTime).isSame(dayStart, "day")
          ),
    [data, dayStart]
  );

  return { slots, isLoading: isLoading || isFetching };
};
